import {serverDomain} from "../constants";
import {fetchBackend, getGETUrl} from "../utils/helperFunctions";
import {ClientICD, clientICDSchema, CachedFeatureToggleStates, FeatureToggleStates} from "sharedlibs";
import KeycloakService from "../KeycloakService";
import Query from "../models/Query";
import {SortModel} from "../components/Tables/types";

/**
 * Reads the feature toggle states as cached in our own DB (kept fresh by a biweekly
 * cron job, or a manual refresh) instead of calling out to the ICN instance.
 */
export async function fetchFeatureSwitchStates(solId: string): Promise<CachedFeatureToggleStates> {
  const response = await fetch(`${serverDomain}/software/getFeatureToggleStates/${solId}`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${KeycloakService.instance.getToken()}`
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch feature toggle states: ${response.status}`)
  }
  return await response.json()
}

/**
 * Fetches the feature toggle states live from the ICN instance and persists them as
 * the new cache. Backs the manual "fetch now" button on the product page.
 */
export async function refreshFeatureSwitchStates(solId: string): Promise<CachedFeatureToggleStates> {
  const response = await fetch(`${serverDomain}/software/refresh-feature-toggle-states/${solId}`, {
    method: 'POST',
    headers: {
      "Authorization": `Bearer ${KeycloakService.instance.getToken()}`
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to refresh feature toggle states: ${response.status}`)
  }
  return await response.json()
}

/**
 * Sends only the toggles the admin changed. The ICN instance leaves every other toggle as it
 * is, so a stale cached value for an untouched toggle is never written back to the instance.
 */
export async function updateFeatureSwitchStates(solId: string, changedFeatureToggleStates: Partial<FeatureToggleStates>): Promise<void> {
  const body = {
    id: solId,
    changedFeatureToggleStates
  }

  const response = await fetch(`${serverDomain}/software/updateFeatureToggleStates`, {
    method: 'PUT',
    headers: {
      "Authorization": `Bearer ${KeycloakService.instance.getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    throw new Error(`Failed to update feature toggle states: ${response.status}`)
  }
}

async function fetchForClientICD(url: string): Promise<ClientICD[]> {
  let response;
  try {
    const json = await fetchBackend(url)

    if (Array.isArray(json)) {
      return json.map((elem) => {
        return clientICDSchema.parse(elem)
      })
    } else {
      throw new Error()
    }
  } catch (e) {
    throw new Error('Error while fetching software from server')
  }
}

export async function getSoftwareAtDate(query?: Query): Promise<ClientICD[]> {
  return await fetchForClientICD(getGETUrl("software/at-date", query))
}

/**
 * This function fetches all ICN-Software objects from the server and returns it as JSON
 *
 * @return {Promise<ClientICD[]>}
 */
export async function getSoftware(query?: Query): Promise<ClientICD[]> {
  return await fetchForClientICD(getGETUrl("software", query))
}

export async function loadRefreshCountDate() {
  const response = await fetch(`${serverDomain}/software/lastUpdate`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${KeycloakService.instance.getToken()}`
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to load refresh count date: ${response.status}`)
  }
  return await response.json()
}

/**
 * Adds a Software object to our DB
 *
 * @param softwareObject
 */
export async function addSoftware(softwareObject: ClientICD) {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/software`, {
    method: 'POST',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(softwareObject)
  })


  if (!response.ok) {
    throw new Error(`Could not add inCareNet-HF Object! HTTP status: ${response.status}`);
  }
}


/**
 * Updates a Software object in our DB
 *
 * @param {Object} softwareObject
 * @param {Object} softwareObject.device - The software object.
 * @param {string} softwareObject.user - The user who has done the action.
 * @return {Promise<void>}
 */
export async function updateSoftware(softwareObject: ClientICD): Promise<void> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/software`, {
    method: 'PUT',
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(softwareObject)
  })

  if (!response.ok) {
    throw new Error(`Could not save inCareNet-HF Object! HTTP status: ${response.status}`);
  }
}

/**
 * Deletes a Software object from our DB
 *
 * @param softwareId
 */
export async function deleteSoftware(softwareId: string) {
  const token = KeycloakService.instance.getToken()

  try {
    const response = await fetch(`${serverDomain}/software/${softwareId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`
      },
    })

    if (!response.ok) {
      throw new Error()
    }
  } catch (e) {
    console.error(e)
    throw new Error('Error while deleting a device')
  }
}

export interface TmzNotResponsiveRow {
  customerId: string;
  customerName: string;
  url: string;
  city: string | null;
  unresponsiveFor: string;
}

export interface TmzNoPatientDataRow {
  customerId: string;
  customerName: string;
  url: string;
  city: string | null;
}

export interface TmzReportData {
  total: number;
  /**
   * True when not a single active Datensafe reports patient data — every one
   * either failed its last refresh or came back with all-zero counts. The report
   * flags this instead of silently listing every instance as patient-free, the
   * same way the emailed report does.
   */
  noDataAtAll: boolean;
  notResponsive: TmzNotResponsiveRow[];
  noPatientData: TmzNoPatientDataRow[];
}

export async function getTmzReportData(): Promise<TmzReportData> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/software/tmz-report`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${token}`
    }
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch TMZ report data: ${response.status}`)
  }
  return await response.json()
}

export async function downloadExcel(selectedDate: string, sortModel: SortModel): Promise<Response> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/software/billing-excel`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({selectedDate, sortModel})
  })

  if (!response.ok) {
    throw new Error(`Could not download excel: ${response.status}`);
  }
  return response
}

async function triggerReport(path: string, errorPrefix: string, recipients: string[]): Promise<Response> {
  const token = KeycloakService.instance.getToken()
  const response = await fetch(`${serverDomain}/software/${path}`, {
    method: 'POST',
    headers: {
      "Authorization": `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({recipients}),
  })

  if (!response.ok) {
    let message: string | undefined
    try {
      const json = await response.json()
      message = json?.message
    } catch {
      // ignore
    }
    throw new Error(message ?? `${errorPrefix}: ${response.status}`)
  }
  return response
}

export async function triggerLowImplantsReport(recipients: string[]): Promise<void> {
  await triggerReport('trigger-low-implants-report', 'Failed to send low implants report', recipients)
}

export async function triggerTmzDailyReport(recipients: string[]): Promise<void> {
  await triggerReport('trigger-tmz-daily-report', 'Failed to send TMZ daily report', recipients)
}

/** Sends the test mail. `sapSheetMissing`: it went out without the SAP order sheet, which could not be built. */
export async function triggerMonthlyProductExport(recipients: string[]): Promise<{sapSheetMissing: boolean}> {
  const response = await triggerReport('trigger-monthly-product-export', 'Failed to send monthly product export', recipients)
  const json = await response.json().catch(() => null)
  return {sapSheetMissing: json?.sapSheetMissing === true}
}

// --- Device-count refresh (Postgres-backed runs, polled over HTTP) ---

export interface DeviceRefreshRun {
  id: string
  status: 'running' | 'success' | 'failed'
  trigger: 'manual' | 'cron'
  startedAt: string
  finishedAt: string | null
  completed: number
  total: number
  failedCustomerIds: string[]
  error: string | null
}

export interface TriggerRefreshResult {
  /** false when a refresh was already running somewhere in the cluster (HTTP 409) */
  started: boolean
  run: DeviceRefreshRun | null
}

export async function postRefreshCounts(): Promise<TriggerRefreshResult> {
  const response = await fetch(`${serverDomain}/software/refresh`, {
    method: 'POST',
    headers: {
      "Authorization": `Bearer ${KeycloakService.instance.getToken()}`
    }
  })

  if (response.status === 409) {
    const json = await response.json()
    return {started: false, run: json?.run ?? null}
  }
  if (!response.ok) {
    throw new Error(`Failed to trigger device count refresh: ${response.status}`)
  }
  const json = await response.json()
  return {started: true, run: json?.run ?? null}
}

export async function getRefreshStatus(): Promise<DeviceRefreshRun | null> {
  const response = await fetch(`${serverDomain}/software/refresh/status`, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${KeycloakService.instance.getToken()}`
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch refresh status: ${response.status}`)
  }
  const json = await response.json()
  return json?.run ?? null
}
